import React from "react";
import { useRouter } from "next/router";
import { useForm, Controller } from "react-hook-form";
import { Skeleton } from "@material-ui/lab";
import { Button, CircularProgress, Grid } from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IPartnerUserChangePasswordReq as SaveReq } from "../types";
import { paths } from "../utils";

import { InputPassword } from "src/apps/commons/inputs";

const useStyles = makeStyles({
  button: {
    paddingTop: "16px"
  },
  layoutButton: {
    flex: "1 1 auto"
  },
  skeleton: {
    display: "inline-flex",
    height: "72px",
    width: "100%"
  }
});

interface Props {
  isLoading: boolean;
  isSaving: boolean;
  onSubmit: (req: SaveReq) => void;
}

interface States {
  password?: string;
  confirmPassword?: string;
}

const schema = yup.object().shape({
  password: yup.string().trim().required("กรุณาระบุ Password"),
  confirmPassword: yup
    .string()
    .trim()
    .required("กรุณาระบุ ยืนยัน Password")
    .oneOf([yup.ref("password"), null], "Passwords must match")
});

const ChangePasswordForm: React.FC<Props> = props => {
  const { isLoading, isSaving, onSubmit } = props;
  const classes = useStyles();
  const router = useRouter();

  const defaultFormData = {
    password: "",
    confirmPassword: ""
  };

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<States>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  const handleSubmitForm = React.useCallback(
    (formData: States) => {
      const { password, confirmPassword } = formData;
      if (password && password === confirmPassword) {
        onSubmit({ password });
      }
    },
    [onSubmit]
  );

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)}>
      <Grid container alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="password"
              control={control}
              defaultValue={defaultFormData.password}
              render={({ field: { onChange, value } }) => (
                <InputPassword
                  disabled={isSaving}
                  error={errors.password?.message}
                  onChange={onChange}
                  value={value}
                  label="Password *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="confirmPassword"
              control={control}
              defaultValue={defaultFormData.confirmPassword}
              render={({ field: { onChange, value } }) => (
                <InputPassword
                  disabled={isSaving}
                  error={errors.confirmPassword?.message}
                  onChange={onChange}
                  value={value}
                  label="Confirm Password *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item className={classes.layoutButton} />

        <Grid item className={classes.button}>
          <Button
            variant="contained"
            onClick={() => router.push(paths.index)}
            disabled={isSaving || isLoading}
          >
            Cancel
          </Button>
        </Grid>

        <Grid item className={classes.button}>
          <Button
            variant="contained"
            color="primary"
            disabled={isSaving || isLoading}
            type="submit"
            startIcon={
              isSaving ? (
                <CircularProgress color="secondary" size={20} />
              ) : (
                <SaveIcon />
              )
            }
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ChangePasswordForm;
